'use client';

import { useParams, useRouter } from 'next/navigation';
import React, { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocations } from '@/hooks/locations/use-locations';
import { NotificationsProvider } from '@/provider/notifications-provider';

import type { Tables } from '@/types/supabase';

type Location = Tables<'locations'>;

interface DashboardState {
  locations: Location[];
  location?: Location | null;
  locationId?: string;
  setLocation: (location?: Location | null) => void;
}

export const DashboardContext = createContext<DashboardState>({} as DashboardState);

export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }

  return context;
};

interface DashboardProviderProps {
  children: React.ReactNode;
  defaultLocationId?: string;
}

export function DashboardProvider({ children }: DashboardProviderProps) {
  const router = useRouter();
  const { data: locations, isLoading } = useLocations();
  const [locationId, setLocationIdFromRoute] = useState<string | undefined>(undefined);
  const [routeReady, setRouteReady] = useState(false);

  const setLocationId = useCallback(
    (newLocationId: string | null) => {
      if (!newLocationId) return;

      const path = window.location.pathname.split('/');
      path[1] = newLocationId;

      if (path.length <= 2 || !path[2]) {
        router.push(`/${newLocationId}/sales`);
      } else {
        router.push(path.join('/'));
      }
    },
    [router]
  );

  const location = useMemo(
    () => locations?.data?.find(loc => loc && loc.slug === locationId) ?? null,
    [locationId, locations]
  );

  useEffect(() => {
    if (!routeReady || isLoading || locations?.data === null) return;

    const firstLocationWithSlug = locations?.data?.find(loc => loc && loc.slug);

    if (!locationId) {
      setLocationId(firstLocationWithSlug?.slug ?? null);
      return;
    }

    const matched = locations?.data?.find(loc => loc && loc.slug === locationId);

    if (!matched) {
      setLocationId(firstLocationWithSlug?.slug ?? null);
    }
  }, [routeReady, locationId, locations, isLoading, setLocationId]);

  const onLocationId = useCallback((id?: string) => {
    setLocationIdFromRoute(id);
    setRouteReady(true);
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        locations: locations?.data || [],
        location,
        locationId: locationId || undefined,
        setLocation: loc => setLocationId(loc?.slug || null),
      }}
    >
      <Suspense fallback={null}>
        <DashboardRouteSync onLocationId={onLocationId} />
      </Suspense>
      <NotificationsWrapper>{children}</NotificationsWrapper>
    </DashboardContext.Provider>
  );
}

function DashboardRouteSync({ onLocationId }: { onLocationId: (id?: string) => void }) {
  const { locationId } = useParams<{ locationId?: string }>();

  useEffect(() => {
    onLocationId(locationId);
  }, [locationId, onLocationId]);

  return null;
}

function NotificationsWrapper({ children }: { children: React.ReactNode }) {
  const { location } = useDashboard();
  return <NotificationsProvider locationId={location?.id}>{children}</NotificationsProvider>;
}
